import React, { ChangeEvent, useEffect, useCallback } from 'react'
import { LabeledInput, LabeledSelect, AutoSuggest } from '@apex/component-library'
import { HStack, Box } from '@chakra-ui/react'
import { LocationOptions } from '../../containers/WireTransfers/Types'
import { FormikErrors, FormikTouched } from 'formik'
import { usStatesList } from '../../constants'
import { COLUMN_WIDTH, MAX_COLUMN_WIDTH } from '../../utils'

interface FormikValues {
  thirdPartyLocation?: LocationOptions
  thirdPartyCountry?: string
  thirdPartyAddress?: string
  thirdPartyAddressLine2?: string
  thirdPartyCity?: string
  thirdPartyState?: string
  thirdPartyZip?: string
}

interface Props {
  formikValues: FormikValues
  formikBlur: (e: React.FocusEvent<any>) => void
  formikTouched: FormikTouched<FormikValues>
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
  setTouched: (fields: object, validate: boolean) => void
  errors: FormikErrors<FormikValues>
  countryList: { value: string }[]
  countryListLoading: boolean
  countryListFailedMessage?: string
}

const WireThirdPartyForm = ({
  formikValues,
  setFieldValue,
  formikTouched,
  formikBlur,
  errors,
  setTouched,
  countryList,
  countryListLoading,
  countryListFailedMessage
}: Props) => {
  const {
    thirdPartyLocation,
    thirdPartyCountry,
    thirdPartyAddress,
    thirdPartyAddressLine2,
    thirdPartyCity,
    thirdPartyState,
    thirdPartyZip
  } = formikValues

  const clearThirdPartyForm = () => {
    setFieldValue('thirdPartyLocation', LocationOptions.Domestic)
    setFieldValue('thirdPartyCountry', 'United States')
    setFieldValue('thirdPartyCountryCode', 'USA')
    setFieldValue('thirdPartyAddress', '')
    setFieldValue('thirdPartyAddressLine2', '')
    setFieldValue('thirdPartyCity', '')
    setFieldValue('thirdPartyState', '')
    setFieldValue('thirdPartyZip', '')
    setFieldValue('beneficiaryName', '')
    setTouched({}, true)
  }

  const clearThirdPartyFormMemo = useCallback(clearThirdPartyForm, [])

  useEffect(() => {
    return clearThirdPartyFormMemo
  }, [clearThirdPartyFormMemo])

  const handleChangeLocation = (e: ChangeEvent<HTMLSelectElement>): void => {
    setFieldValue('thirdPartyLocation', e.target.value)
    if (e.target.value === LocationOptions.Domestic) {
      setFieldValue('thirdPartyCountry', 'United States')
      setFieldValue('thirdPartyCountryCode', 'USA')
      setFieldValue('thirdPartyAddress', '')
      setFieldValue('thirdPartyAddressLine2', '')
      setFieldValue('thirdPartyCity', '')
    } else {
      setFieldValue('thirdPartyCountry', '')
      setFieldValue('thirdPartyAddress', '')
      setFieldValue('thirdPartyAddressLine2', '')
      setFieldValue('thirdPartyCity', '')
      setFieldValue('thirdPartyZip', '')
      setFieldValue('thirdPartyState', '')
      setTouched({}, true)
    }
  }

  return (
    <>
      <HStack spacing={8}>
        <LabeledSelect
          label='Location'
          name='thirdPartyLocation'
          onBlur={formikBlur}
          isRequired
          onChange={handleChangeLocation}
          value={thirdPartyLocation}
        >
          <option value={LocationOptions.Domestic}>Domestic</option>
          <option value={LocationOptions.International}>International</option>
        </LabeledSelect>
        {thirdPartyLocation === LocationOptions.International ? (
          <Box width='100%'>
            <AutoSuggest
              label='Country'
              name='thirdPartyCountry'
              inputValue={thirdPartyCountry || ''}
              onBlur={formikBlur}
              onChange={(e) => {
                setFieldValue('thirdPartyCountry', e.target.value)
              }}
              onSelectItem={(val) => setFieldValue('thirdPartyCountry', val)}
              dataSet={countryList}
              isDisabled={countryListLoading || !!countryListFailedMessage}
              placeholder={countryListLoading ? 'Loading...' : ''}
              errorMessage={countryListFailedMessage || errors?.thirdPartyCountry}
              isInvalid={!!countryListFailedMessage || (formikTouched.thirdPartyCountry && !!errors?.thirdPartyCountry)}
              isRequired
            />
          </Box>
        ) : (
          <LabeledInput label='Country' name='thirdPartyCountry' value={'United States'} isRequired isReadOnly />
        )}
      </HStack>
      <LabeledInput
        label='Address'
        name='thirdPartyAddress'
        onBlur={formikBlur}
        isRequired
        value={thirdPartyAddress}
        onChange={(e) => {
          setFieldValue('thirdPartyAddress', e.target.value)
        }}
        errorMessage={errors?.thirdPartyAddress}
        isInvalid={formikTouched.thirdPartyAddress && !!errors?.thirdPartyAddress}
      />
      <LabeledInput
        label='Address Line 2'
        name='thirdPartyAddressLine2'
        onBlur={formikBlur}
        value={thirdPartyAddressLine2}
        onChange={(e) => {
          setFieldValue('thirdPartyAddressLine2', e.target.value)
        }}
        errorMessage={errors?.thirdPartyAddressLine2}
        isInvalid={formikTouched.thirdPartyAddressLine2 && !!errors?.thirdPartyAddressLine2}
      />
      <HStack spacing={8}>
        <LabeledInput
          label='City'
          isRequired
          name='thirdPartyCity'
          onBlur={formikBlur}
          value={thirdPartyCity}
          onChange={(e) => {
            setFieldValue('thirdPartyCity', e.target.value)
          }}
          errorMessage={errors?.thirdPartyCity}
          isInvalid={formikTouched.thirdPartyCity && !!errors?.thirdPartyCity}
        />
        {thirdPartyLocation === LocationOptions.Domestic && (
          <LabeledSelect
            label='State'
            isRequired
            name='thirdPartyState'
            onBlur={formikBlur}
            value={thirdPartyState}
            onChange={(e) => {
              setFieldValue('thirdPartyState', e.target.value)
            }}
            isInvalid={formikTouched.thirdPartyState && !!errors?.thirdPartyState}
            errorMessage={errors?.thirdPartyState}
          >
            <option value=''></option>
            {usStatesList.map((state, index) => (
              <option key={`${index}${state.key}`} value={state.key}>
                {state.name}
              </option>
            ))}
          </LabeledSelect>
        )}
      </HStack>
      {thirdPartyLocation === LocationOptions.Domestic && (
        <LabeledInput
          width={COLUMN_WIDTH}
          maxW={MAX_COLUMN_WIDTH}
          label='ZIP Code'
          name='thirdPartyZip'
          value={thirdPartyZip}
          isRequired
          onBlur={formikBlur}
          onChange={(e) => {
            setFieldValue('thirdPartyZip', e.target.value.replace(/[^0-9-]*/g, ''))
          }}
          errorMessage={errors?.thirdPartyZip}
          isInvalid={formikTouched.thirdPartyZip && !!errors?.thirdPartyZip}
        />
      )}
    </>
  )
}

export default WireThirdPartyForm
